/**
 * AI Analytics Dashboard Component
 *
 * Displays AI agent performance metrics:
 * - Call volume trends
 * - Average call duration
 * - Resolution rates
 * - Common intents
 * - Peak hours heatmap
 * - Follow-up completion rates
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3,
  Clock,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Phone,
  Calendar,
  Target
} from 'lucide-react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { AIConversation } from '@/types/ai-support';

const log = createComponentLogger('AIAnalytics', 'ai-support');

interface AnalyticsMetrics {
  totalCalls: number;
  avgDuration: number;
  resolutionRate: number;
  followupRate: number;
  topIntents: Array<{ intent: string; count: number }>;
  hourlyDistribution: Array<{ hour: number; count: number }>;
  dailyTrend: Array<{ date: string; count: number; avgDuration: number }>;
  sentimentDistribution: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

interface AIAnalyticsProps {
  agentId: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export function AIAnalytics({ agentId, dateRange }: AIAnalyticsProps) {
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState<'today' | '7days' | '30days' | 'custom'>('7days');

  useEffect(() => {
    loadAnalytics();
  }, [agentId, selectedRange, dateRange]);

  async function loadAnalytics() {
    try {
      setLoading(true);

      // Calculate date range
      const end = dateRange?.end || new Date();
      const start = dateRange?.start || (() => {
        const date = new Date();
        switch (selectedRange) {
          case 'today':
            date.setHours(0, 0, 0, 0);
            return date;
          case '7days':
            date.setDate(date.getDate() - 7);
            return date;
          case '30days':
            date.setDate(date.getDate() - 30);
            return date;
          default:
            return date;
        }
      })();

      // Fetch analytics data
      const { data, error } = await supabase
        .rpc('get_ai_agent_analytics', {
          p_agent_id: agentId,
          p_start_date: start.toISOString(),
          p_end_date: end.toISOString()
        });

      if (error) {
throw error;
}

      setMetrics(data);
      log.info('Analytics loaded successfully', { agentId, range: selectedRange });
    } catch (error) {
      log.error('Failed to load analytics', { agentId }, error as Error);
    } finally {
      setLoading(false);
    }
  }

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No analytics data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
        <div className="flex gap-2">
          <Button
            variant={selectedRange === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedRange('today')}
          >
            Today
          </Button>
          <Button
            variant={selectedRange === '7days' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedRange('7days')}
          >
            7 Days
          </Button>
          <Button
            variant={selectedRange === '30days' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedRange('30days')}
          >
            30 Days
          </Button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Calls */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
            <Phone className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalCalls}</div>
            <p className="text-xs text-gray-600 mt-1">
              {selectedRange === 'today' ? 'Today' : `Last ${selectedRange === '7days' ? '7' : '30'} days`}
            </p>
          </CardContent>
        </Card>

        {/* Average Duration */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
            <Clock className="h-4 w-4 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(metrics.avgDuration)}</div>
            <p className="text-xs text-gray-600 mt-1">Per conversation</p>
          </CardContent>
        </Card>

        {/* Resolution Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Resolution Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {metrics.resolutionRate}%
            </div>
            <p className="text-xs text-gray-600 mt-1">Resolved without follow-up</p>
          </CardContent>
        </Card>

        {/* Requires Follow-up */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Follow-up Needed</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {Math.round(metrics.totalCalls * (1 - metrics.resolutionRate / 100))}
            </div>
            <p className="text-xs text-gray-600 mt-1">{metrics.followupRate}% completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Call Volume Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-end justify-between gap-2">
            {metrics.dailyTrend.map((day, index) => {
              const maxCalls = Math.max(...metrics.dailyTrend.map(d => d.count));
              const height = (day.count / maxCalls) * 100;

              return (
                <div key={index} className="flex-1 flex flex-col items-center gap-2">
                  <div
                    className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer group relative"
                    style={{ height: `${height}%`, minHeight: day.count > 0 ? '20px' : '0' }}
                    title={`${day.count} calls - ${formatDuration(day.avgDuration)} avg`}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {day.count} calls
                    </div>
                  </div>
                  <span className="text-xs text-gray-600">
                    {parseAsLocalDate(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Intents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Common Intents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.topIntents.slice(0, 5).map((item, index) => {
                const maxCount = metrics.topIntents[0]?.count || 1;
                const percentage = (item.count / maxCount) * 100;

                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{item.intent}</span>
                      <span className="text-sm text-gray-600">{item.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sentiment Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Sentiment Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span className="text-sm">Positive</span>
                </div>
                <span className="font-bold">{metrics.sentimentDistribution.positive}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${metrics.sentimentDistribution.positive}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-gray-500" />
                  <span className="text-sm">Neutral</span>
                </div>
                <span className="font-bold">{metrics.sentimentDistribution.neutral}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gray-500 h-2 rounded-full"
                  style={{ width: `${metrics.sentimentDistribution.neutral}%` }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span className="text-sm">Negative</span>
                </div>
                <span className="font-bold">{metrics.sentimentDistribution.negative}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-red-500 h-2 rounded-full"
                  style={{ width: `${metrics.sentimentDistribution.negative}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Hours Heatmap */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Peak Call Hours
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-2">
            {metrics.hourlyDistribution.map((hour) => {
              const maxCount = Math.max(...metrics.hourlyDistribution.map(h => h.count));
              const intensity = hour.count / maxCount;

              return (
                <div
                  key={hour.hour}
                  className="relative group cursor-pointer"
                  title={`${hour.hour}:00 - ${hour.count} calls`}
                >
                  <div
                    className="h-12 rounded transition-colors"
                    style={{
                      backgroundColor: `rgba(37, 99, 235, ${intensity * 0.8 + 0.2})`
                    }}
                  />
                  <div className="text-center mt-1">
                    <span className="text-xs text-gray-600">{hour.hour}</span>
                  </div>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {hour.count} calls
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-4 text-xs text-gray-600">
            <span>12 AM</span>
            <span>6 AM</span>
            <span>12 PM</span>
            <span>6 PM</span>
            <span>11 PM</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
